import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
  CircularProgress,
  Tooltip,
  Button,
  IconButton,
  Link,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SettingsIcon from "@mui/icons-material/Settings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SendIcon from "@mui/icons-material/Send";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import PreviewIcon from "@mui/icons-material/Preview";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaidIcon from "@mui/icons-material/Paid";
import IconRefresh from "./icons/Refresh";
import SearchBar from "./utils/SearchBar";
import CommonDialog from "./utils/Dialogs";
import CustomSnackbar from "./utils/Snakbar";
import { isMobile } from "react-device-detect";
import parse from "html-react-parser";
import { NavLink } from "react-router-dom";
import { withStyles } from "@mui/styles";
import TwintIcon from "./icons/Twint";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

export default function StickyHeadTable(props) {
  const theme = useTheme();
  const {
    superUser,
    columns,
    data,
    onEdit,
    onEdit2,
    onRefreshData,
    onExportData,
    onDelete,
    onItemsForSelectionChange,
    checkboxEnabled,
    onShoppingSnipetRequest,
    onSMSRequest,
    onPdfRequest,
    onSendSMS,
    filters,
    search,
    businessType,
    canEdit,
    selectedItems, // Add prop to control selection externally
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [currentId, setCurrentId] = useState(0);

  const [open, setOpen] = useState(false); // dialog
  const [title, setTitle] = useState(""); // dialog

  const [success, setSuccess] = useState(false); //snack
  const [textSnack, setTextSnack] = useState(""); //snack

  const [selected, setRowSelected] = useState([]);

  // Synchronize internal selection with external prop
  useEffect(() => {
    if (
      selectedItems !== undefined &&
      selectedItems.length === 0 &&
      selected.length > 0
    ) {
      setRowSelected([]);
    }
  }, [selectedItems]);

  const [searchValue, setSearchValue] = useState(search || "");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleDialog = (open, title, mode, id) => {
    if (id) {
      setCurrentId(id); // to handle the local confirmation delete
    }

    if (mode === "edit") {
      onEdit(id);
    } else {
      // assume : delete action
      setOpen(open);
      setTitle(title);
    }
  };

  const confirmDelete = async () => {
    try {
      await onDelete(currentId);
      setTextSnack("Article supprimé");
      setCurrentId(0);
      setRowSelected([]);
      onItemsForSelectionChange && onItemsForSelectionChange([]);
      setOpen(false);
      handleSnackbar(true);
    } catch (error) {
      setTextSnack("Erreur lors de la suppression de l'article!");
      setOpen(false);
      handleSnackbar(true);
    }
  };

  const cancelDelete = async () => {
    setCurrentId(0);
    setOpen(false);
  };

  const handleSnackbar = (bool) => {
    setSuccess(bool);
  };

  const DeleteConfirmationComponent = () => {
    return (
      <React.Fragment>
        Vous êtes sur le point de supprimer l'article #{currentId}, continuer ?
        <div style={{ margin: "20px 0" }}>
          <Button onClick={() => confirmDelete()}>Oui</Button>
          <Button onClick={() => cancelDelete()}>Annuler</Button>
        </div>
      </React.Fragment>
    );
  };

  const handleRowClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setRowSelected(newSelected);
    onItemsForSelectionChange && onItemsForSelectionChange(newSelected);
  };

  const handleSelectAllClick = (event) => {
    let newSelecteds = [];
    if (event.target.checked) {
      newSelecteds = rows.map((r) => r.id);
      setRowSelected(newSelecteds);
      onItemsForSelectionChange && onItemsForSelectionChange(newSelecteds);
      return;
    }
    setRowSelected([]);
    onItemsForSelectionChange && onItemsForSelectionChange(newSelecteds);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleUpdateSearch = (value) => {
    setSearchValue(value);
  };

  const findInRow = (row, value) => {
    let found = false;
    filters.forEach((filter) => {
      let val = row[filter];
      if (typeof val === "string") {
        if (val.toLowerCase().indexOf(value.toLowerCase().trim()) !== -1) {
          found = true;
        }
      }

      if (val && typeof val === "object") {
        for (const obj of val) {
          if (
            obj.name &&
            obj.name.toLowerCase().indexOf(value.toLowerCase().trim()) !== -1
          ) {
            found = true;
          }
        }
      }
    });
    return found;
  };

  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  const treatRows = (data) => {
    try {
      return (
        (Array.isArray(data) && data.map((d) => d)).filter((r) => {
          return findInRow(r, searchValue);
        }) || []
      );
    } catch (error) {
      console.log("error", error);
      return [];
    }
  };

  const rows = treatRows(data);

  const isItems = rows.some((d) => !!d.eventPlanner);

  const getLinkToSiteWithTransaction = (row) => {
    const sale = rows.find((r) => r.saleId === row.saleId);
    const transactionId = sale.saleUppTransactionNumber || "undefined!";
    const referer = sale.saleReferer || "undefined!";
    return `${referer.replace("#/", "")}?payment_intent=${transactionId}&redirect_status=succeeded`;
  };

  const getSaleCreationDate = (row) => {
    const sale = rows.find((r) => r.saleId === row.saleId);
    return sale.saleCreated;
  };

  const getCustomerEmail = (row) => {
    const user = rows.find((r) => r.userId === row.userId);
    return user.userEmail !== "" ? user.userEmail : null;
  };

  const getParsedSaleNote = (row) => {
    const sale = rows.find((r) => r.saleId === row.saleId);
    // return parseSaleNote(sale.dimensions);
    return sale.userMobile;
  };

  const getCustomerSummary = (row) => {
    const sale = rows.find((r) => r.saleId === row.saleId);
    // const concatName = `${sale.userFirstname} ${sale.userLastname} (${sale.customerId})`;
    const concatName = `${(sale.userFirstname || "").toLowerCase()} ${(sale.userLastname || "").toLowerCase()} (${sale.customerId})`;
    return concatName !== " " ? concatName : null;
  };

  const getCustomerMobile = (row) => {
    const sale = rows.find((r) => r.saleId === row.saleId);
    return sale.userMobile;
  };

  const preventDeleteItem = (row) => {
    return row.busy > 0 || parseInt(row.demo, 10) === 0;
  };

  const getStatusClass = (value) => {
    if (value === "success") {
      return "success";
    }
    if (value === "opened") {
      return "open";
    }
    if (value === "pending") {
      return "pending";
    }
    if (value === "#error") {
      return "failed";
    }
    return "";
  };

  const styles = (theme) => ({
    root: { width: "100%" },
    chip: { margin: theme.spacing(0.5) },
    chipize2: { margin: theme.spacing(0.5), fontSize: 13 },
    search: { margin: theme.spacing(2.5, "auto") },
    success: { background: "#bcffbc", padding: "5px 9px", borderRadius: 7 },
    open: { background: "#d8eafd", padding: "5px 9px", borderRadius: 7 },
    pending: { background: "#fff1bc", padding: "5px 9px", borderRadius: 7 },
    failed: {
      background: "#ef7b7b",
      color: "white",
      padding: "5px 9px",
      borderRadius: 7,
    },
    saleDateMobile: { fontSize: 11, display: "block", marginTop: 8 },
    tablecellHeaderMobile: { fontSize: 14 },
    tablecellMobile: { fontSize: 14 },
    imgItem: { height: 40 },
  });

  const classes = styles(theme);

  const getStatusStyle = (status) => {
    return styles[status] || {};
  };

  useEffect(() => {
    if (search) {
      setSearchValue(search);
    }

    if (Array.isArray(data)) {
      const timeout = setTimeout(() => {
        setHasFetchedData(true);
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [search, data]);

  return isLoading && !hasFetchedData ? (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 50 }}>
      <CircularProgress />
    </div>
  ) : (
    <Paper style={styles.root}>
      <CustomSnackbar
        text={textSnack}
        open={success}
        onCloseSnakbar={setSuccess}
      />
      <CommonDialog
        onClose={() => setOpen(false)}
        title={title}
        open={open}
        render={() => (
          <>
            Confirmer suppression ?
            <div style={{ margin: "20px 0" }}>
              <Button onClick={() => confirmDelete()}>Oui</Button>
              <Button onClick={() => cancelDelete()}>Annuler</Button>
            </div>
          </>
        )}
      />
      <SearchBar search={searchValue} onUpdateSearch={setSearchValue} />
      <Button onClick={onRefreshData}>
        <IconRefresh />
      </Button>
      {onExportData ? (
        <Button onClick={onExportData}>
          <FileDownloadIcon />
        </Button>
      ) : null}

      {!isMobile ? (
        <Button
          disabled={selected.length < 1}
          onClick={() =>
            handleDialog(true, "Supprimer un article", "delete", selected)
          }
        >
          <DeleteIcon />
        </Button>
      ) : null}

      <div>
        {rows.length < 1 ? (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <h3>Aucun enregistrement trouvé</h3>
          </div>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {checkboxEnabled ? (
                  <TableCell>
                    <Checkbox
                      checked={
                        rows.length > 0 && selected.length === rows.length
                      }
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                ) : null}
                {columns.map((column) => (
                  <TableCell
                    // style={isMobile ? classes.tablecellHeaderMobile : null}
                    // style={{ minWidth: column.minWidth }}
                    // merge those two styles
                    style={{
                      ...((isMobile && classes.tablecellHeaderMobile) || {}),
                      minWidth: column.minWidth || 100,
                    }}
                    key={column.id}
                    align={column.align}
                  >
                    {column.id === "action" && isMobile ? (
                      <SettingsIcon />
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, key) => {
                  const isItemSelected = isSelected(row.id);
                  return (
                    <TableRow
                      key={key}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      // key={row.id}
                      onClick={(event) => handleRowClick(event, row.id)}
                    >
                      {checkboxEnabled ? (
                        <TableCell>
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": null }}
                          ></Checkbox>
                        </TableCell>
                      ) : null}

                      {columns.map((column) => {
                        const value = row[column.id] || "";

                        return column.id === "action" ? (
                          <TableCell key={column.id}>
                            <IconButton
                              aria-label="Editer"
                              title="Editer"
                              onClick={() =>
                                handleDialog(
                                  true,
                                  "Editer l'article",
                                  "edit",
                                  row.id
                                )
                              }
                            >
                              <EditIcon />
                            </IconButton>
                            {businessType === "sms" ? (
                              <IconButton
                                aria-label="Lancer la campagne SMS"
                                title="Lancer la campagne SMS"
                                onClick={() => onSendSMS(row.id)}
                              >
                                <SmsRoundedIcon />
                              </IconButton>
                            ) : null}
                            {businessType === "sales" &&
                            onPdfRequest &&
                            row.saleStatus === "success" ? (
                              <IconButton
                                aria-label="Regénérer le PDF"
                                title="Regénérer le PDF"
                                onClick={() => {
                                  onPdfRequest(row);
                                }}
                              >
                                <PictureAsPdfIcon />
                              </IconButton>
                            ) : null}

                            {isItems ? (
                              <IconButton
                                aria-label="Créer un ticket"
                                title="Créer un ticket"
                                onClick={() => onEdit2(row.id)}
                              >
                                <AddCircleIcon />
                              </IconButton>
                            ) : null}

                            {!superUser ? (
                              <IconButton
                                disabled={preventDeleteItem(row)}
                                aria-label="Supprimer"
                                title="Supprimer"
                                onClick={() =>
                                  handleDialog(
                                    true,
                                    "Supprimer un article",
                                    "delete",
                                    row.id
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : null}

                            {onShoppingSnipetRequest ? (
                              <IconButton
                                aria-label="code du site"
                                onClick={() => onShoppingSnipetRequest(row)}
                              >
                                <ShoppingCartIcon />
                              </IconButton>
                            ) : null}

                            {columns.find((c) => c.id === "ticketCode") ? (
                              <IconButton
                                aria-label="renvoyer le SMS"
                                title="renvoyer le SMS"
                                onClick={() => onSMSRequest(row)}
                              >
                                <SendIcon />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        ) : (
                          <TableCell
                            style={{
                              ...((isMobile && classes.tablecellMobile) || {}),
                              minWidth: column.minWidth || 100,
                              maxWidth: column.maxWidth || "auto",
                              wordWrap: "break-word",
                            }}
                            key={column.id}
                            align={column.align}
                          >
                            {column.format && typeof value === "number" ? (
                              column.format(value)
                            ) : column.id === "apikey" ? (
                              `${value.substr(0, 5)}...`
                            ) : column.id === "files" ? (
                              Array.isArray(value) ? (
                                value.map((v, k) => {
                                  const url =
                                    process.env.REACT_APP_BUCKETS3 + v;
                                  return (
                                    <img
                                      style={classes.imgItem}
                                      key={k}
                                      alt={`${v}`}
                                      src={url}
                                    />
                                  );
                                })
                              ) : (
                                value
                              )
                            ) : column.id === "platforms" ? (
                              Array.isArray(value) ? (
                                value.map((v, i) => {
                                  let icon;
                                  return (
                                    <Chip
                                      // style={{ fontSize: "10px" }}
                                      key={i}
                                      icon={icon}
                                      label={
                                        v &&
                                        v.name &&
                                        v.name.replace("https://", "")
                                      }
                                      // onDelete={data.label === 'React' ? undefined : handleDelete(data)}
                                      style={classes.chip}
                                    />
                                  );
                                })
                              ) : (
                                ""
                              )
                            ) : column.id === "website" ? (
                              <div>
                                {value}
                                <div>
                                  {row.metadata?.affiliatedItems.map(
                                    (item, index) => (
                                      <Chip
                                        key={index}
                                        label={item.title}
                                        style={{
                                          ...classes.chip,
                                          fontSize: 11,
                                        }}
                                      />
                                    )
                                  )}
                                </div>
                              </div>
                            ) : column.id === "referer" ? (
                              value.replace("https://", "")
                            ) : column.id === "compliant" ? (
                              value.length === 0 ? (
                                <Tooltip title="Article conforme">
                                  <IconButton aria-label="Article conforme">
                                    <DoneIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <HtmlTooltip
                                  title={
                                    <React.Fragment>
                                      {value.map((v) => v).join(" | ")}
                                    </React.Fragment>
                                  }
                                >
                                  <IconButton aria-label="delete">
                                    <ErrorIcon />
                                  </IconButton>
                                </HtmlTooltip>
                              )
                            ) : column.id === "ticketCode" ? (
                              <React.Fragment>
                                {canEdit ? (
                                  <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={
                                      "https://code.mayan-payments.com:5000/f/c=" +
                                      value +
                                      "&h=MTAzZ&0"
                                    }
                                  >
                                    {value}
                                  </a>
                                ) : (
                                  value
                                )}

                                <div>
                                  <Chip
                                    key="0"
                                    label={
                                      rows.find(
                                        (r) => r.ticketId === row.ticketId
                                      ).mobile
                                    }
                                    style={classes.chip}
                                  />
                                </div>
                              </React.Fragment>
                            ) : column.id === "name" &&
                              businessType === "sms" ? (
                              <React.Fragment>
                                {value}
                                <div style={{ fontSize: "88%" }}>
                                  {row.metadata &&
                                  row.metadata.campaignItemSelected ? (
                                    <span
                                      style={{
                                        marginRight: 6,
                                        // backgroundColor: "#e3e3e3ff",
                                        padding: "2px 5px",
                                        borderRadius: 4,
                                      }}
                                    >
                                      #{row.metadata.campaignItemSelected}
                                    </span>
                                  ) : null}
                                  {row.metadata && row.metadata.nbUsers ? (
                                    <span
                                      style={{
                                        marginRight: 6,
                                        // backgroundColor: "#e3e3e3ff",
                                        padding: "2px 5px",
                                        borderRadius: 4,
                                      }}
                                    >
                                      <PeopleIcon
                                        fontSize="small"
                                        style={{ marginBottom: -6 }}
                                      />
                                      &nbsp;{row.metadata.nbUsers}
                                    </span>
                                  ) : null}
                                  {row.metadata && row.metadata.costSMS ? (
                                    <span
                                      style={{
                                        marginRight: 6,
                                        // backgroundColor: "#e3e3e3ff",
                                        padding: "2px 5px",
                                        borderRadius: 4,
                                      }}
                                    >
                                      Chf {row.metadata.costSMS}
                                    </span>
                                  ) : null}
                                  <span
                                    style={{
                                      marginRight: 6,
                                      // backgroundColor: "#e3e3e3ff",
                                      padding: "2px 5px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    <span>Test: </span>
                                    <span
                                      style={{
                                        color: row.metadata?.isTestMode
                                          ? "green"
                                          : "red",
                                      }}
                                    >
                                      {row.metadata?.isTestMode ? "oui" : "non"}
                                    </span>
                                  </span>
                                </div>
                              </React.Fragment>
                            ) : column.id === "titleFormatted" &&
                              businessType === "sales" ? (
                              <React.Fragment>
                                {parse(isMobile ? `${value}` : value)}
                                {getCustomerSummary(row) ? (
                                  <div>
                                    <Chip
                                      key={column.id + "1"}
                                      label={getCustomerSummary(row)}
                                      style={{
                                        ...classes.chip,
                                        maxWidth: "150px",
                                      }}
                                    />
                                    <Chip
                                      key={column.id + "2"}
                                      label={getCustomerMobile(row)}
                                      style={{
                                        ...classes.chip,
                                        maxWidth: "150px",
                                      }}
                                    />
                                  </div>
                                ) : null}
                              </React.Fragment>
                            ) : column.id === "userMobile" &&
                              businessType === "customers" ? (
                              <React.Fragment>
                                {parse(value)}
                                {getCustomerEmail(row) ? (
                                  <div>
                                    <Chip
                                      key="0"
                                      label={getCustomerEmail(row)}
                                      style={classes.chip}
                                    />
                                  </div>
                                ) : null}
                                {row.userSales && row.userSales.length > 0 ? (
                                  <div>
                                    {row.userSales.map((sale) => (
                                      <Chip
                                        key={sale.saleId}
                                        label={`Sale#${sale.saleId}`}
                                        style={classes.chip}
                                      />
                                    ))}
                                  </div>
                                ) : null}
                              </React.Fragment>
                            ) : column.id === "saleStatus" &&
                              businessType === "sales" ? (
                              <React.Fragment>
                                <span style={classes[getStatusClass(value)]}>
                                  {parse(value)}
                                </span>
                                {isMobile ? (
                                  <div style={classes.saleDateMobile}>
                                    {getSaleCreationDate(row)}
                                  </div>
                                ) : null}

                                {row.salePaymentMethodName === "twint" ? (
                                  <div>
                                    <br />
                                    <TwintIcon></TwintIcon>
                                  </div>
                                ) : (
                                  <div>
                                    <br />
                                    <CreditCardIcon></CreditCardIcon>
                                  </div>
                                )}
                              </React.Fragment>
                            ) : column.id === "saleRefNumber" &&
                              businessType === "sales" ? (
                              <React.Fragment>
                                {/* <div>{value}</div> */}
                                <div style={{ fontSize: "0.8em" }}>
                                  <div>{row.saleUppTransactionNumber}</div>
                                  <Link
                                    // ?payment_intent=pi_3PH6qi2OnERiScNb0Gt8xewV&redirect_status=succeeded
                                    href={getLinkToSiteWithTransaction(row)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <PreviewIcon></PreviewIcon>
                                  </Link>
                                </div>
                              </React.Fragment>
                            ) : column.id === "saleId" &&
                              businessType === "tickets" ? (
                              <NavLink to={`/sales/${value}`}>{value}</NavLink>
                            ) : businessType === "items" &&
                              column.id === "title" ? (
                              <React.Fragment>
                                <div>{value}</div>
                                {row.recurrent === 1 ? (
                                  <SubscriptionsIcon />
                                ) : null}
                              </React.Fragment>
                            ) : column.id === "committed" ? (
                              <React.Fragment>
                                {value === "1" ? <PaidIcon /> : null}
                              </React.Fragment>
                            ) : (
                              parse(value)
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </div>
      <TablePagination
        rowsPerPageOptions={[25, 50, 200]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          "aria-label": "previous page",
        }}
        nextIconButtonProps={{
          "aria-label": "next page",
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
